import { createContext, useContext, useEffect, useState } from "react";
import { ExternalLink, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import EmptyCellHelpLink from "../EmptyCellHelpLink";
import CellProvenance from "../CellProvenance";
import { api } from "@/lib/api";

/**
 * Contexts that let every DataRow render an EmptyCellHelpLink for
 * blank cells without threading `degreeId` / `sectionId` through 100+
 * callsites. DegreeDetail wraps the whole page with
 * <DegreePageProvider>, each <Section> emits its own
 * <SectionKeyContext>, and DataRow reads both.
 *
 * The provider ALSO fetches the field-edit audit trail for this
 * degree once on mount and exposes it via `auditByField` — a map
 * keyed `${section}::${field_label}`. `CellProvenance` reads it to
 * decorate cells with "Verified by University of X" chips. Zero N+1
 * — one fetch per page load.
 */
export const DegreePageContext = createContext({
  degreeId: null,
  universityId: null,
  auditByField: null,
});
const SectionKeyContext = createContext(null);

export function DegreePageProvider({ degreeId, universityId, children }) {
  const [auditByField, setAuditByField] = useState(null);

  useEffect(() => {
    if (!degreeId) {
      setAuditByField(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/degrees/${degreeId}/field-edits`);
        if (cancelled) return;
        const m = new Map();
        for (const row of data.items || []) {
          const key = `${row.section || ""}::${row.field_label || ""}`;
          if (!m.has(key)) m.set(key, row);   // API returns newest-first
        }
        setAuditByField(m);
      } catch {
        if (!cancelled) setAuditByField(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, [degreeId]);

  return (
    <DegreePageContext.Provider value={{ degreeId, universityId, auditByField }}>
      {children}
    </DegreePageContext.Provider>
  );
}

/**
 * DegreeDetail internal building blocks — extracted from
 * pages/DegreeDetail.jsx on 2026-02-28 to slim the parent page.
 *
 * These four helpers are entirely internal to the degree page:
 *   - Ev         → tiny evidence/citation footer
 *   - SectionNav → sticky in-page nav (uses the shared SECTION_MENU)
 *   - Section    → wrapped <section> with anchor + display heading
 *   - DataRow    → the "key · value" row used all through the page
 *
 * Nothing else in the app imports them, so consolidating here keeps
 * DegreeDetail.jsx focused on the actual page composition.
 */

export function Ev({ ev }) {
  if (!ev) return null;
  const color = ev.confidence === "verified" ? "text-emerald-700"
    : ev.confidence === "not_published" ? "text-stone-400" : "text-stone-500";
  return (
    <div className={`text-[10px] font-mono mt-1 ${color}`}>
      <span className="uppercase tracking-wider">{ev.confidence}</span>
      {ev.source ? ` · ${ev.source}` : ""}
      {ev.date_checked ? ` · checked ${ev.date_checked}` : ""}
      {ev.url ? <a href={ev.url} target="_blank" rel="noreferrer" className="ml-2 underline"><ExternalLink className="h-3 w-3 inline" /></a> : null}
    </div>
  );
}

// Ordered list of anchor sections — drives the sticky nav at the top of
// the page AND provides IDs for scroll-into-view. Kept in one place so
// the nav can never drift out of sync with the section list below.
export const SECTION_MENU = [
  { id: "warnings",       n: "!",  label: "Warnings",       optional: true },
  { id: "identity",       n: "1",  label: "Identity" },
  { id: "entry",          n: "2",  label: "Entry" },
  { id: "admissions",     n: "3",  label: "Admissions" },
  { id: "curriculum",     n: "4",  label: "Curriculum" },
  { id: "teaching",       n: "5",  label: "Teaching" },
  { id: "accreditation",  n: "6",  label: "Accreditation" },
  { id: "placement",      n: "7",  label: "Placements" },
  { id: "outcomes",       n: "8",  label: "Outcomes" },
  { id: "cost",           n: "9",  label: "Cost" },
  { id: "scholarships",   n: "10", label: "Scholarships" },
  { id: "international",  n: "11", label: "International" },
  { id: "application",    n: "13", label: "Application" },
  { id: "evidence",       n: "15", label: "Evidence" },
];

export function SectionNav({ hasWarnings, hiddenSections }) {
  const [active, setActive] = useState(null);
  const hidden = hiddenSections instanceof Set
    ? hiddenSections
    : new Set(hiddenSections || []);

  useEffect(() => {
    const ids = SECTION_MENU
      .filter(s => !s.optional || (s.id === "warnings" && hasWarnings))
      .filter(s => !hidden.has(s.id))
      .map(s => s.id);
    // Highlight the section closest to the top of the viewport (roughly).
    const onScroll = () => {
      let best = null;
      let bestTop = Number.NEGATIVE_INFINITY;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        // Anything that has crossed the sticky bar (roughly 140px from top)
        // counts as "active". The bottom-most such section wins.
        if (top <= 140 && top > bestTop) {
          bestTop = top;
          best = id;
        }
      }
      setActive(best);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasWarnings, hidden]);

  const items = SECTION_MENU
    .filter(s => !s.optional || (s.id === "warnings" && hasWarnings))
    .filter(s => !hidden.has(s.id));

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 88;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <nav
      data-testid="degree-section-nav"
      className="sticky top-14 z-30 -mx-4 sm:mx-0 mt-6 mb-2 px-4 py-3 bg-[#FAF7F1]/95 backdrop-blur border-y border-stone-200"
    >
      <div className="flex gap-1 overflow-x-auto no-scrollbar">
        {items.map((s) => {
          const isActive = active === s.id;
          const isWarning = s.id === "warnings";
          return (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              data-testid={`nav-${s.id}`}
              className={
                "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono tracking-wide transition-colors " +
                (isActive
                  ? (isWarning
                    ? "bg-red-700 text-white"
                    : "bg-[#4C1D95] text-white")
                  : (isWarning
                    ? "text-red-700 hover:bg-red-50"
                    : "text-stone-600 hover:bg-stone-100"))
              }
            >
              <span className={"text-[10px] " + (isActive ? "opacity-80" : "opacity-60")}>{s.n}</span>
              <span>{s.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export function Section({ title, id, children }) {
  // Split "3 · Admissions competitiveness" → number + label so we can
  // render the number as a muted eyebrow and the label as a real
  // display heading.
  const parts = title.split(" · ");
  const num = parts.length > 1 ? parts[0] : null;
  const label = parts.length > 1 ? parts.slice(1).join(" · ") : title;
  return (
    <SectionKeyContext.Provider value={id}>
      <section
        id={id}
        data-testid={`section-${id}`}
        className="scroll-mt-28 pt-6 mt-8 first:mt-2 border-t border-stone-200"
      >
        {num && (
          <div className="text-[11px] font-underground-sc tracking-[0.18em] text-stone-500">
            Section {num}
          </div>
        )}
        <h3 className="font-editorial text-2xl sm:text-[26px] leading-tight text-stone-900 mt-1">
          {label}
        </h3>
        <div className="mt-4">{children}</div>
      </section>
    </SectionKeyContext.Provider>
  );
}

// Sections where empty cells should surface a "help us improve" link
// (owner brief Feb 2026, design_guidelines.json §6). Warnings /
// Identity / Evidence stay silent because a bare `—` there IS the
// signal (e.g. "no warnings on file" is a good thing).
const HELPABLE_SECTIONS = new Set([
  "admissions",
  "curriculum",
  "outcomes",
  "cost",
  "international",
  "scholarships",
  "placement",
]);

// Values that count as "empty" for the help-link affordance. Trims
// whitespace and treats the bare em-dash / en-dash sentinel we
// scatter across the codebase as blank.
function _isEmptyCell(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") {
    const s = v.trim();
    if (s.length === 0) return true;
    if (s === "—" || s === "–" || s === "-") return true;
    // "Not on file" / "Not published" / "Unknown" — human-readable
    // sentinels the page uses when a data provider skipped a cell.
    // Keep the string but also offer the help link.
    return false;
  }
  return false;
}

export function DataRow({ k, v, note, tooltip, testId }) {
  const { degreeId, universityId } = useContext(DegreePageContext);
  const sectionId = useContext(SectionKeyContext);
  const isEmpty = _isEmptyCell(v);
  const showHelpLink =
    isEmpty && degreeId && sectionId && HELPABLE_SECTIONS.has(sectionId);
  return (
    <div className="data-row" data-testid={testId}>
      <div className="data-key flex items-center gap-1.5">
        {k}
        {tooltip && <DataRowTooltip content={tooltip} slug={k} />}
      </div>
      <div className="data-val">
        {isEmpty ? (
          showHelpLink ? (
            <span className="inline-flex items-center gap-2">
              <span className="text-stone-400">—</span>
              <EmptyCellHelpLink
                degreeId={degreeId}
                universityId={universityId}
                section={sectionId}
                field={typeof k === "string" ? k : undefined}
              />
            </span>
          ) : (
            "—"
          )
        ) : (
          <>
            {v}
            {typeof k === "string" && sectionId && (
              <CellProvenance section={sectionId} field={k} />
            )}
          </>
        )}
        {note && (
          <div className="text-[10px] text-stone-500 font-underground-sc tracking-wider mt-0.5">
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

function DataRowTooltip({ content, slug }) {
  const testid = `tooltip-row-${(slug || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`Explain ${slug}`}
            data-testid={testid}
            className="text-stone-400 hover:text-[#4C1D95] transition"
          >
            <Info className="h-3 w-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs bg-[#002147] text-white text-[11px] leading-relaxed p-3 whitespace-normal normal-case"
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
